import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { planKeys } from '@entities/Plan';
import {
  answerGrammarItem,
  answerGrammarReview,
  fetchGrammarReview,
  buyGrammarAnalyses,
  fetchGrammarExercises,
  fetchGrammarLesson,
  fetchGrammarTopics,
  markGrammarLessonDone,
  openGrammarExercise,
  type GrammarAnswer,
  type GrammarExercise,
  type GrammarExercises,
  type GrammarLessonResponse,
  type GrammarTopics,
} from '../api/grammarApi';

export const grammarKeys = {
  all: ['grammar'] as const,
  topics: ['grammar', 'topics'] as const,
  lesson: (slug: string) => ['grammar', 'lesson', slug] as const,
  exercises: (slug: string) => ['grammar', 'exercises', slug] as const,
  review: ['grammar', 'review'] as const,
};

/** Подборка повторения на сегодня (главная и экран подборки). */
export function useGrammarReviewQuery(enabled = true) {
  return useQuery({ queryKey: grammarKeys.review, queryFn: fetchGrammarReview, enabled, staleTime: 60_000 });
}

/** Ответ в подборке: последний ответ пересчитывает расписание и может принести жетон — перечитываем тропинку и баланс. */
export function useGrammarReviewAnswerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ key, text }: { key: string; text: string }) => answerGrammarReview(key, text),
    onSuccess: (res) => {
      if (!res.done) return;
      void qc.invalidateQueries({ queryKey: grammarKeys.review });
      void qc.invalidateQueries({ queryKey: grammarKeys.topics });
      if (res.reward?.amount) {
        void qc.invalidateQueries({ queryKey: planKeys.quota });
        void qc.invalidateQueries({ queryKey: planKeys.billing });
      }
    },
  });
}

export function useGrammarTopicsQuery() {
  return useQuery({ queryKey: grammarKeys.topics, queryFn: fetchGrammarTopics, staleTime: 30_000 });
}

/**
 * Список тем только из кэша, без запроса: урок берёт из него правило «освоена» (`mastery`), когда ученик пришёл с
 * тропинки. Пришёл по ссылке — кэша нет, тексты берут 3 и 7, как при старом сервере.
 */
export function useGrammarTopicsCache() {
  return useQuery({ queryKey: grammarKeys.topics, queryFn: fetchGrammarTopics, enabled: false, staleTime: 30_000 });
}

/**
 * «Урок дочитан» на сервере (§ 8, B2) — один раз, когда ученик впервые дошёл до последнего шага. Сбой — молча:
 * отметка всё равно есть в этом браузере. Успех правит кэши урока и списка тем без перечитывания.
 */
export function useGrammarLessonDoneMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => markGrammarLessonDone(slug),
    onSuccess: () => {
      qc.setQueryData<GrammarLessonResponse>(grammarKeys.lesson(slug), (data) =>
        data ? { ...data, topic: { ...data.topic, progress: { ...data.topic.progress, lesson_done: true } } } : data,
      );
      qc.setQueryData<GrammarTopics>(grammarKeys.topics, (data) =>
        data
          ? {
              ...data,
              topics: data.topics.map((topic) =>
                topic.slug === slug ? { ...topic, progress: { ...topic.progress, lesson_done: true } } : topic,
              ),
            }
          : data,
      );
    },
  });
}

export function useGrammarLessonQuery(slug: string) {
  return useQuery({ queryKey: grammarKeys.lesson(slug), queryFn: () => fetchGrammarLesson(slug), enabled: !!slug, retry: false });
}

export function useGrammarExercisesQuery(slug: string) {
  return useQuery({ queryKey: grammarKeys.exercises(slug), queryFn: () => fetchGrammarExercises(slug), enabled: !!slug });
}

/** Заменить упражнение в кэше списка — после открытия или ответа список не перечитываем. */
function patchExercise(data: GrammarExercises | undefined, no: number, patch: (ex: GrammarExercise) => GrammarExercise) {
  if (!data) return data;
  return { ...data, exercises: data.exercises.map((ex) => (ex.no === no ? patch(ex) : ex)) };
}

export function useOpenGrammarExerciseMutation(slug: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (no: number) => openGrammarExercise(slug, no),
    onSuccess: ({ charged, exercise }) => {
      qc.setQueryData<GrammarExercises>(grammarKeys.exercises(slug), (data) => patchExercise(data, exercise.no, () => exercise));
      void qc.invalidateQueries({ queryKey: grammarKeys.topics });
      // Списали жетон — баланс в меню должен это показать.
      if (charged > 0) {
        void qc.invalidateQueries({ queryKey: planKeys.quota });
        void qc.invalidateQueries({ queryKey: planKeys.billing });
      }
    },
  });
}

export function useGrammarAnswerMutation(slug: string, no: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, answer }: { itemId: string; answer: string }) => answerGrammarItem(slug, no, itemId, answer),
    onSuccess: (res: GrammarAnswer) => {
      qc.setQueryData<GrammarExercises>(grammarKeys.exercises(slug), (data) =>
        patchExercise(data, no, (ex) => ({
          ...ex,
          ...res.exercise,
          analyses: res.analyses,
          items: ex.items?.map((item) =>
            item.id === res.item_id
              ? { ...item, state: { tries: (item.state?.tries ?? 0) + 1, solved: res.solved || !!item.state?.solved, last: res.verdict } }
              : item,
          ) ?? null,
        })),
      );
      // Список тем на экране упражнения нужен только для «Следующая тема»: помечаем устаревшим, но не перечитываем на
      // каждый ответ — тропинка и главная перечитают его при открытии.
      void qc.invalidateQueries({ queryKey: grammarKeys.topics, refetchType: 'none' });
      // Тема только что пройдена — шапка урока при возврате сразу показывает «пройдена» (§ 3.3, п. 7).
      if (res.just_passed) void qc.invalidateQueries({ queryKey: grammarKeys.lesson(slug) });
      // Награда за темы — несгораемые жетоны: баланс в меню должен их показать.
      if (res.reward?.amount) {
        void qc.invalidateQueries({ queryKey: planKeys.quota });
        void qc.invalidateQueries({ queryKey: planKeys.billing });
      }
    },
  });
}

export function useBuyGrammarAnalysesMutation(slug: string, no: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => buyGrammarAnalyses(slug, no, requestId),
    onSuccess: ({ charged, analyses }) => {
      qc.setQueryData<GrammarExercises>(grammarKeys.exercises(slug), (data) => patchExercise(data, no, (ex) => ({ ...ex, analyses })));
      if (charged > 0) {
        void qc.invalidateQueries({ queryKey: planKeys.quota });
        void qc.invalidateQueries({ queryKey: planKeys.billing });
      }
    },
  });
}
